import React, { useState, useEffect, useCallback, useRef, Suspense } from "react";
import ReactMarkdown from "react-markdown";
import ViewerNavbar from "./ViewerNavbar";
import API, { ensureOrthancAuthCookie } from "../../services/api.ts";
import { getMediaUrl } from "../../services/media.ts";
import "./CustomViewer.css";
import { resolveAllowedViewerUrl } from '../../utils/safeUrl';

// Lazy-load SignalChart to avoid loading chart.js dependencies if not needed
const SignalChart = React.lazy(() => import("./SignalChart"));

// Viewer type to data type mapping
const VIEWER_DATA_SUPPORT = {
    'ohif': ['dicom'],
    'ohif-segmentation': ['dicom'],
    'ohif-3d': ['dicom'],
    'ohif-microscopy': ['dicom'],
    'kitware-volview': ['dicom'],
    'stone-web-viewer': ['dicom'],
    'trame-slicer': ['dicom'],
    'dv2': ['dicom', 'image', 'document'],
    'discovery-viewer-test': ['image', 'document'],
    'image-viewer': ['image'],
    'document-viewer': ['document'],
    'signal-viewer': ['signal'],
    'pme': ['pathology'],
    'cvat': ['video'],
    'ehr-viewer': ['ehr'],
};

const IMAGE_ZOOM_MIN = 0.25;
const IMAGE_ZOOM_MAX = 8;
const IMAGE_ZOOM_STEP = 1.25;
const clampImageZoom = (z) => Math.min(IMAGE_ZOOM_MAX, Math.max(IMAGE_ZOOM_MIN, z));

// Image panel content with zoom + scroll-to-pan, used inside CustomViewer's
// embedded image-viewer panels. The full standalone ImageViewer page has its
// own copy of this logic; this component is the equivalent for the constrained
// panel layout where zoom controls overlay the image.
const ImagePanelContent = ({ images, currentIndex, onNavigate }) => {
    const safeIndex = Math.max(0, Math.min(currentIndex, images.length - 1));
    const currentImage = images[safeIndex];

    const scrollRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [natural, setNatural] = useState(null);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

    // Reset zoom when the displayed image changes.
    useEffect(() => {
        setZoom(1);
        setNatural(null);
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ left: 0, top: 0 });
        }
    }, [safeIndex]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => {
            setContainerSize({
                w: entry.contentRect.width,
                h: entry.contentRect.height,
            });
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const zoomIn = useCallback(() => setZoom((z) => clampImageZoom(z * IMAGE_ZOOM_STEP)), []);
    const zoomOut = useCallback(() => setZoom((z) => clampImageZoom(z / IMAGE_ZOOM_STEP)), []);
    const zoomReset = useCallback(() => setZoom(1), []);

    // Ctrl/Cmd + wheel zooms toward the cursor; plain wheel scrolls normally.
    const handleWheel = useCallback((e) => {
        if (!(e.ctrlKey || e.metaKey)) return;
        e.preventDefault();
        const container = scrollRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const pointerX = e.clientX - rect.left + container.scrollLeft;
        const pointerY = e.clientY - rect.top + container.scrollTop;
        setZoom((prev) => {
            const next = clampImageZoom(prev * (e.deltaY < 0 ? IMAGE_ZOOM_STEP : 1 / IMAGE_ZOOM_STEP));
            if (next === prev) return prev;
            const ratio = next / prev;
            requestAnimationFrame(() => {
                if (!container) return;
                container.scrollLeft = pointerX * ratio - (e.clientX - rect.left);
                container.scrollTop = pointerY * ratio - (e.clientY - rect.top);
            });
            return next;
        });
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);

    if (!currentImage) {
        return (
            <div className="custom-no-content">
                <i className="fa fa-image"></i>
                <p>No images available</p>
            </div>
        );
    }

    // Fit the image to whichever axis runs out first; zoom multiplies that.
    let imageStyle = {};
    if (natural && containerSize.w && containerSize.h) {
        const fit = Math.min(
            containerSize.w / natural.w,
            containerSize.h / natural.h,
            1,
        );
        const w = natural.w * fit * zoom;
        const h = natural.h * fit * zoom;
        imageStyle = { width: `${w}px`, height: `${h}px`, maxWidth: 'none', maxHeight: 'none' };
    }

    return (
        <div className="custom-panel-content image-content">
            <div className="image-viewer-wrapper image-panel-scroll" ref={scrollRef}>
                <div className="image-panel-inner">
                    <img
                        src={getMediaUrl(currentImage.filePath)}
                        alt={currentImage.fileName}
                        className="viewer-image"
                        style={imageStyle}
                        onLoad={(e) => setNatural({
                            w: e.target.naturalWidth,
                            h: e.target.naturalHeight,
                        })}
                        draggable={false}
                    />
                </div>

                {images.length > 1 && safeIndex > 0 && (
                    <button
                        className="image-nav-btn prev-btn"
                        onClick={() => onNavigate(-1)}
                    >
                        <i className="fa fa-chevron-left"></i>
                    </button>
                )}
                {images.length > 1 && safeIndex < images.length - 1 && (
                    <button
                        className="image-nav-btn next-btn"
                        onClick={() => onNavigate(1)}
                    >
                        <i className="fa fa-chevron-right"></i>
                    </button>
                )}

                <div className="image-zoom-toolbar">
                    <button
                        type="button"
                        className="image-zoom-btn"
                        onClick={zoomOut}
                        disabled={zoom <= IMAGE_ZOOM_MIN + 1e-6}
                        title="Zoom out ( - )"
                        aria-label="Zoom out"
                    >
                        <i className="fa fa-search-minus"></i>
                    </button>
                    <button
                        type="button"
                        className="image-zoom-level"
                        onClick={zoomReset}
                        title="Reset zoom ( 0 )"
                        aria-label="Reset zoom"
                    >
                        {Math.round(zoom * 100)}%
                    </button>
                    <button
                        type="button"
                        className="image-zoom-btn"
                        onClick={zoomIn}
                        disabled={zoom >= IMAGE_ZOOM_MAX - 1e-6}
                        title="Zoom in ( + )"
                        aria-label="Zoom in"
                    >
                        <i className="fa fa-search-plus"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

const CustomViewer = () => {
    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const studyIdParam = urlParams.get('study_id');
    const patientIdParam = urlParams.get('patient_id');
    const patientIdsParam = urlParams.get('patient_ids');
    const patientDataStorageKey = urlParams.get('patient_data_storage_key');
    const customViewerIdParam = urlParams.get('custom_viewer_id');
    const customViewerConfigParam = urlParams.get('custom_viewer_config');
    const viewerNameParam = urlParams.get('viewer_name');
    const dicomDataParam = urlParams.get('dicom_data');
    const imageDataParam = urlParams.get('image_data');
    const documentDataParam = urlParams.get('document_data');
    const signalDataParam = urlParams.get('signal_data');
    const videoDataParam = urlParams.get('video_data');
    const ehrDataParam = urlParams.get('ehr_data');
    const viewAsUserIdParam = urlParams.get('view_as_user_id');

    const [studyId] = useState(studyIdParam || localStorage.getItem('studyId'));
    const [patientId] = useState(patientIdParam || 'Unknown');
    // Parse multiple patient IDs if provided
    const [patientIds] = useState(() => {
        if (patientIdsParam) {
            return patientIdsParam.split(',').filter(id => id.trim());
        }
        return patientIdParam ? [patientIdParam] : [];
    });
    const [currentPatientIndex] = useState(() => {
        if (patientIdsParam && patientIdParam) {
            const ids = patientIdsParam.split(',').filter(id => id.trim());
            const idx = ids.indexOf(patientIdParam);
            return idx >= 0 ? idx : 0;
        }
        return 0;
    });
    // Parse patient data map for navigation (from sessionStorage to avoid URI too large)
    const [patientDataMap] = useState(() => {
        if (patientDataStorageKey) {
            try {
                const storedData = sessionStorage.getItem(patientDataStorageKey);
                if (storedData) {
                    return JSON.parse(storedData);
                }
            } catch (e) {
                console.error('Error parsing patient data map from storage:', e);
                return {};
            }
        }
        return {};
    });
    const [viewerConfig, setViewerConfig] = useState(null);
    const [viewerName, setViewerName] = useState(viewerNameParam || 'Custom Viewer');
    const [loading, setLoading] = useState(true);

    // Study data states (labels, questions, scores)
    const [studyName, setStudyName] = useState('');
    const [labels, setLabels] = useState([]);
    const [labelDisplayMode, setLabelDisplayMode] = useState('dropdown');
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);
    const [viewerNavbarConfig, setViewerNavbarConfig] = useState({});

    // Data states
    const [dicomData, setDicomData] = useState([]);
    const [dicomUrls, setDicomUrls] = useState({}); // Store URLs for different viewer types
    const [imageData, setImageData] = useState([]);
    const [documentData, setDocumentData] = useState([]);
    const [signalData, setSignalData] = useState([]);
    const [videoData, setVideoData] = useState([]);
    const [ehrData, setEhrData] = useState([]);
    const [ehrExpandedSections, setEhrExpandedSections] = useState({});
    const [cvatUrls, setCvatUrls] = useState({}); // panelId -> iframe URL
    const [cvatLaunching, setCvatLaunching] = useState({}); // panelId -> boolean

    // Signal annotations (time-range regions)
    const [signalAnnotations, setSignalAnnotations] = useState([]);

    // Panel states for navigation
    const [panelStates, setPanelStates] = useState({});

    // Parse config from URL
    useEffect(() => {
        if (customViewerConfigParam) {
            try {
                const config = JSON.parse(customViewerConfigParam);
                setViewerConfig(config);
            } catch (err) {
                console.error("Error parsing viewer config:", err);
            }
        }
    }, [customViewerConfigParam]);

    // Fetch study data (labels, questions, scores)
    const fetchStudyData = useCallback(async () => {
        if (!studyId) return;
        try {
            const response = await API.get(`/studies/${studyId}/`);
            if (response.data) {
                setStudyName(response.data.name || '');
                setLabels(response.data.labels || []);
                setLabelDisplayMode(response.data.label_display_mode || 'dropdown');
                setQuestions(response.data.questions || []);
                setScores(response.data.scores || []);
                setShareAnnotations(response.data.share_annotations || false);
                setViewerNavbarConfig(response.data.viewer_navbar_config || {});
            }
        } catch (err) {
            console.error("Error fetching study data:", err);
        }
    }, [studyId]);

    // Fetch viewer details if we have an ID
    useEffect(() => {
        const fetchViewerDetails = async () => {
            if (!customViewerIdParam) return;
            try {
                const response = await API.get(`/viewers/${customViewerIdParam}/`);
                setViewerName(response.data.name);
                if (!viewerConfig && response.data.config) {
                    setViewerConfig(response.data.config);
                }
            } catch (err) {
                console.error("Error fetching viewer details:", err);
            }
        };
        fetchViewerDetails();
    }, [customViewerIdParam, viewerConfig]);

    // Parse and fetch DICOM data for all viewer types
    const fetchDicomData = useCallback(async () => {
        if (!dicomDataParam) return;

        const items = dicomDataParam.split(',').map(item => {
            const [id, source] = item.split(':');
            return { id: id.trim(), source: source?.trim() || 'MedicalImage' };
        }).filter(item => item.id);

        // Separate MedicalImage IDs from PatientData IDs to avoid ID collisions
        const medicalImageIds = items
            .filter(item => item.source === 'MedicalImage')
            .map(item => parseInt(item.id))
            .filter(id => !isNaN(id));
        const patientDataIds = items
            .filter(item => item.source === 'PatientData')
            .map(item => parseInt(item.id))
            .filter(id => !isNaN(id));

        if (medicalImageIds.length === 0 && patientDataIds.length === 0) return;

        // Ensure orthanc_auth cookie is fresh before fetching viewer URLs
        try {
            await ensureOrthancAuthCookie();
        } catch (err) {
            console.warn('[CustomViewer] Could not refresh auth cookie:', err);
        }

        // Get unique DICOM viewer types from the config
        const dicomViewerTypes = ['ohif', 'ohif-segmentation', 'ohif-3d', 'ohif-microscopy', 'trame-slicer'];
        const configViewerTypes = viewerConfig?.panels
            ?.map(p => p.viewer_type)
            ?.filter(vt => dicomViewerTypes.includes(vt)) || ['ohif'];

        // Always include 'ohif' as default, plus any specific types from config
        const viewerTypesToFetch = [...new Set(['ohif', ...configViewerTypes])];

        // Build the request payload with proper separation of ID types
        const payload = { viewer_name: '' };
        if (medicalImageIds.length > 0) payload.data_ids = medicalImageIds;
        if (patientDataIds.length > 0) payload.patient_data_ids = patientDataIds;
        // Pass study_id so launch_viewer can use study-level DICOM copies
        if (studyId) payload.study_id = parseInt(studyId);
        // Pass view_as_user_id for admin viewing as another user
        if (viewAsUserIdParam) payload.view_as_user_id = parseInt(viewAsUserIdParam);

        const urls = {};
        for (const viewerType of viewerTypesToFetch) {
            try {
                const response = await API.post('/viewers/launch_viewer/', {
                    ...payload,
                    viewer_name: viewerType
                });
                urls[viewerType] = response.data.viewer_url;
            } catch (err) {
                console.error(`Error launching ${viewerType} viewer:`, err);
            }
        }

        setDicomUrls(urls);
        setDicomData([{ ids: [...medicalImageIds, ...patientDataIds] }]);
    }, [dicomDataParam, viewerConfig, studyId, viewAsUserIdParam]);

    // Parse and fetch image data
    const fetchImageData = useCallback(async () => {
        if (!imageDataParam) return;

        const items = imageDataParam.split(',').map(item => {
            const [id, source] = item.split(':');
            return { id: id.trim(), source: source?.trim() || 'MedicalImage' };
        }).filter(item => item.id);

        if (items.length === 0) return;

        const imagesArray = [];
        for (const imageItem of items) {
            try {
                if (imageItem.source === 'PatientData') {
                    const response = await API.get(`/data_manager/patient-data/${imageItem.id}/`);
                    const itemData = response.data;
                    imagesArray.push({
                        ...itemData,
                        filePath: itemData.file || itemData.location,
                        fileName: itemData.filename || 'Image'
                    });
                } else {
                    const imageResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/`);
                    const imgData = imageResponse.data;
                    const filesResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/files/`);
                    const filesData = filesResponse.data;

                    if (filesData.files && filesData.files.length > 0) {
                        imagesArray.push({
                            ...imgData,
                            filePath: filesData.files[0].path,
                            fileName: filesData.files[0].name
                        });
                    }
                }
            } catch (err) {
                console.error(`Error fetching image ${imageItem.id}:`, err);
            }
        }
        setImageData(imagesArray);
    }, [imageDataParam]);

    // Parse and fetch document data
    // Uses study-scoped endpoints for PatientData documents so access control
    // is consistent with study membership (not the more restrictive PatientData queryset).
    const fetchDocumentData = useCallback(async () => {
        if (!documentDataParam) {
            console.log('[CustomViewer] No document_data URL param, skipping document fetch');
            return;
        }

        console.log('[CustomViewer] Fetching documents from param:', documentDataParam, 'studyId:', studyId);
        const items = documentDataParam.split(',').map(item => {
            const [id, source] = item.split(':');
            return { id: id.trim(), source: source?.trim() || 'MedicalDocument' };
        }).filter(item => item.id);

        if (items.length === 0) return;

        const documentsArray = [];
        for (const docItem of items) {
            try {
                if (docItem.source === 'PatientData') {
                    // Use study-scoped endpoint for consistent access control
                    const itemUrl = studyId
                        ? `/studies/${studyId}/patient-data/${docItem.id}/`
                        : `/data_manager/patient-data/${docItem.id}/`;
                    const response = await API.get(itemUrl);
                    const itemData = response.data;

                    // Determine document type from file extension or original_file_type
                    const filename = itemData.filename || '';
                    const fileExt = filename.split('.').pop()?.toLowerCase() || '';
                    const docType = itemData.original_file_type || fileExt || '';
                    const textTypes = ['txt', 'md', 'text', 'markdown'];
                    const isTextDoc = textTypes.includes(docType) || textTypes.includes(fileExt);

                    // Fetch content for text files using study-scoped endpoint
                    let content = '';
                    if (isTextDoc) {
                        try {
                            const contentUrl = studyId
                                ? `/studies/${studyId}/patient-data/${docItem.id}/content/`
                                : `/data_manager/patient-data/${docItem.id}/content/`;
                            const contentResponse = await API.get(contentUrl);
                            if (contentResponse.data && contentResponse.data.content) {
                                content = contentResponse.data.content;
                            }
                        } catch (e) {
                            console.error(`[CustomViewer] Error fetching PatientData ${docItem.id} content:`, e);
                        }
                    }

                    documentsArray.push({
                        ...itemData,
                        filePath: itemData.file,
                        fileName: itemData.filename || 'Document',
                        documentType: docType,
                        content
                    });
                } else {
                    const docResponse = await API.get(`/data_manager/medical-documents/${docItem.id}/`);
                    const docData = docResponse.data;

                    // Determine document type
                    const docType = docData.document_type?.toLowerCase() ||
                                   docData.file?.split('.').pop()?.toLowerCase() || '';
                    const textTypes = ['txt', 'md', 'text', 'markdown'];
                    const isTextDoc = textTypes.includes(docType);

                    // Fetch content for text files using API endpoint
                    let content = '';
                    if (isTextDoc) {
                        try {
                            const contentResponse = await API.get(`/data_manager/medical-documents/${docItem.id}/content/`);
                            if (contentResponse.data && contentResponse.data.content) {
                                content = contentResponse.data.content;
                            }
                        } catch (e) {
                            console.error(`[CustomViewer] Error fetching MedicalDocument ${docItem.id} content:`, e);
                        }
                    }

                    documentsArray.push({
                        ...docData,
                        filePath: docData.file,
                        fileName: docData.title || docData.file?.split('/').pop() || 'Document',
                        documentType: docType,
                        content
                    });
                }
            } catch (err) {
                console.error(`[CustomViewer] Error fetching document ${docItem.id} (${docItem.source}):`, err.response?.status, err.message);
            }
        }
        console.log(`[CustomViewer] Loaded ${documentsArray.length} documents`);
        setDocumentData(documentsArray);
    }, [documentDataParam, studyId]);

    // Parse and fetch signal data
    const fetchSignalData = useCallback(async () => {
        if (!signalDataParam) {
            console.log('[CustomViewer] No signal_data URL param, skipping signal fetch');
            return;
        }

        console.log('[CustomViewer] Fetching signals from param:', signalDataParam, 'studyId:', studyId);
        const items = signalDataParam.split(',').map(item => {
            const [id, source] = item.split(':');
            return { id: id.trim(), source: source?.trim() || 'PatientData' };
        }).filter(item => item.id);

        if (items.length === 0) return;

        const signalsArray = [];
        for (const signalItem of items) {
            try {
                // Fetch basic info
                console.log(`[CustomViewer] Fetching patient-data/${signalItem.id}/...`);
                const infoResponse = await API.get(`/data_manager/patient-data/${signalItem.id}/`);
                const data = infoResponse.data;
                console.log(`[CustomViewer] PatientData info:`, data);

                // Fetch signal metadata
                console.log(`[CustomViewer] Fetching signal-content metadata for ${signalItem.id}...`);
                const metaResponse = await API.get(`/data_manager/patient-data/${signalItem.id}/signal-content/?metadata_only=true`);
                const metadata = metaResponse.data.metadata;
                console.log(`[CustomViewer] Signal metadata:`, metadata);

                signalsArray.push({
                    id: signalItem.id,
                    patient_id: data.patient_id,
                    filename: data.filename,
                    description: data.description,
                    metadata: metadata,
                });
            } catch (err) {
                console.error(`[CustomViewer] Error fetching signal ${signalItem.id}:`, err.response?.status, err.message);
            }
        }
        console.log(`[CustomViewer] Loaded ${signalsArray.length} signals`);
        setSignalData(signalsArray);
    }, [signalDataParam, studyId]);

    // Parse video data items (for CVAT panels)
    const fetchVideoData = useCallback(async () => {
        if (!videoDataParam) return;

        const items = videoDataParam.split(',').map(item => {
            const [id, source] = item.split(':');
            return { id: id.trim(), source: source?.trim() || 'PatientData' };
        }).filter(item => item.id);

        if (items.length === 0) return;
        setVideoData(items.map(item => ({ id: parseInt(item.id), source: item.source })));
    }, [videoDataParam]);

    // Parse and fetch EHR data
    const fetchEhrData = useCallback(async () => {
        if (!ehrDataParam) return;

        const items = ehrDataParam.split(',').map(item => {
            const [id, source] = item.split(':');
            return { id: id.trim(), source: source?.trim() || 'PatientData' };
        }).filter(item => item.id);

        if (items.length === 0) return;

        const records = [];
        for (const item of items) {
            try {
                const response = await API.get(`/data_manager/patient-data/${item.id}/`);
                const data = response.data;
                let content = {};

                if (data.file) {
                    try {
                        const contentResponse = await API.get(`/data_manager/patient-data/${item.id}/content/`);
                        const text = contentResponse.data.content;
                        try {
                            content = JSON.parse(text);
                        } catch {
                            if (data.original_file_type === 'csv' || data.filename?.toLowerCase().endsWith('.csv')) {
                                const lines = text.trim().split('\n');
                                if (lines.length > 0) {
                                    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
                                    const rows = [];
                                    for (let i = 1; i < lines.length; i++) {
                                        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                                        const row = {};
                                        headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
                                        rows.push(row);
                                    }
                                    content = { headers, rows, _format: 'csv' };
                                }
                            } else {
                                content = { raw_content: text, _format: 'text' };
                            }
                        }
                    } catch (parseErr) {
                        console.warn("Could not parse EHR file:", parseErr);
                        content = { error: `Could not load file: ${parseErr.message}` };
                    }
                }

                records.push({
                    id: item.id,
                    patient_id: data.patient_id,
                    filename: data.filename,
                    content: content
                });
            } catch (err) {
                console.error(`Error fetching EHR record ${item.id}:`, err);
            }
        }
        setEhrData(records);
    }, [ehrDataParam]);

    // Launch CVAT for video panels — calls /cvat/launch/ and builds auto-login iframe URL
    const launchCvatForPanel = useCallback(async (panelId) => {
        if (!studyId || videoData.length === 0) return;
        if (cvatUrls[panelId]) return; // Already launched
        if (cvatLaunching[panelId]) return; // Already launching

        setCvatLaunching(prev => ({ ...prev, [panelId]: true }));
        try {
            const dataIds = videoData.map(v => ({
                id: v.id,
                source_model: v.source || 'PatientData',
                data_type: 'video',
            }));

            const resp = await API.post('/cvat/launch/', {
                study_id: parseInt(studyId),
                data_ids: dataIds,
                data_type: 'video',
            });

            const taskId = resp.data.cvat_task_id;
            const jobId = resp.data.cvat_job_id;

            if (taskId) {
                const targetPath = jobId
                    ? `/tasks/${taskId}/jobs/${jobId}`
                    : `/tasks/${taskId}`;
                const cvatPort = '19444';
                const cvatBaseUrl = `https://${window.location.hostname}:${cvatPort}`;
                const cvatUrl = `${cvatBaseUrl}/discovery-auth/?next=${encodeURIComponent(targetPath)}`;
                setCvatUrls(prev => ({ ...prev, [panelId]: cvatUrl }));
            }
        } catch (err) {
            console.error('[CustomViewer] Error launching CVAT:', err);
        } finally {
            setCvatLaunching(prev => ({ ...prev, [panelId]: false }));
        }
    }, [studyId, videoData, cvatUrls, cvatLaunching]);

    // Load existing signal annotations for the current patient
    const loadSignalAnnotations = useCallback(async () => {
        if (!studyId || signalData.length === 0) return;
        try {
            const response = await API.get(`/studies/${studyId}/annotations/`);
            const annotations = response.data;
            // Use first patient's annotations
            const firstPatient = signalData[0]?.patient_id;
            const patientAnnotation = annotations.find(
                a => a.patient_id === firstPatient && a.annotated_by === undefined
            ) || annotations.find(a => a.patient_id === firstPatient);

            if (patientAnnotation?.question_answers?._signal_regions) {
                setSignalAnnotations(patientAnnotation.question_answers._signal_regions);
            } else {
                setSignalAnnotations([]);
            }
        } catch (err) {
            console.error("[CustomViewer] Error loading signal annotations:", err);
        }
    }, [studyId, signalData]);

    // Save signal annotations
    const saveSignalAnnotations = useCallback(async (regions) => {
        if (!studyId || signalData.length === 0) return;
        try {
            const firstPatient = signalData[0]?.patient_id;
            await API.post(`/studies/${studyId}/annotate/`, {
                patient_id: firstPatient,
                question_answers: { _signal_regions: regions },
            });
        } catch (err) {
            console.error("[CustomViewer] Error saving signal annotations:", err);
        }
    }, [studyId, signalData]);

    // Fetch all data on mount
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([
                fetchStudyData(),
                fetchDicomData(),
                fetchImageData(),
                fetchDocumentData(),
                fetchSignalData(),
                fetchVideoData(),
                fetchEhrData()
            ]);
            setLoading(false);
        };
        loadData();
    }, [fetchStudyData, fetchDicomData, fetchImageData, fetchDocumentData, fetchSignalData, fetchVideoData, fetchEhrData]);

    // Load signal annotations when signal data is available
    useEffect(() => {
        loadSignalAnnotations();
    }, [loadSignalAnnotations]);

    // Count document viewer panels
    const getDocumentPanelCount = useCallback(() => {
        if (!viewerConfig?.panels) return 0;
        return viewerConfig.panels.filter(p => p.viewer_type === 'document-viewer').length;
    }, [viewerConfig]);

    // Initialize panel states with document distribution
    useEffect(() => {
        if (viewerConfig?.panels) {
            const initialStates = {};
            const documentPanelCount = viewerConfig.panels.filter(p => p.viewer_type === 'document-viewer').length;
            let documentPanelIndex = 0;

            viewerConfig.panels.forEach(panel => {
                if (panel.viewer_type === 'document-viewer') {
                    // Distribute documents across panels: panel 0 gets doc 0, panel 1 gets doc 1, etc.
                    initialStates[panel.id] = {
                        currentIndex: documentPanelIndex,
                        assignedDocumentIndex: documentPanelIndex // Fixed document index for this panel
                    };
                    documentPanelIndex++;
                } else {
                    initialStates[panel.id] = { currentIndex: 0 };
                }
            });
            setPanelStates(initialStates);
        }
    }, [viewerConfig]);

    // Navigate within a panel
    const navigatePanel = (panelId, direction) => {
        setPanelStates(prev => ({
            ...prev,
            [panelId]: {
                ...prev[panelId],
                currentIndex: (prev[panelId]?.currentIndex || 0) + direction
            }
        }));
    };

    // Handle signal annotation creation
    const handleAnnotationCreate = useCallback((startSeconds, endSeconds, label) => {
        const newRegion = {
            start_seconds: Math.round(startSeconds * 100) / 100,
            end_seconds: Math.round(endSeconds * 100) / 100,
            label_id: label.id,
            label_name: label.name,
            color: label.color || '#0d6efd',
        };
        const updated = [...signalAnnotations, newRegion];
        setSignalAnnotations(updated);
        saveSignalAnnotations(updated);
    }, [signalAnnotations, saveSignalAnnotations]);

    // Handle signal annotation deletion
    const handleAnnotationDelete = useCallback((index) => {
        const updated = signalAnnotations.filter((_, i) => i !== index);
        setSignalAnnotations(updated);
        saveSignalAnnotations(updated);
    }, [signalAnnotations, saveSignalAnnotations]);

    // Get data for a specific panel based on its viewer type
    const getPanelData = (viewerType) => {
        const supportedTypes = VIEWER_DATA_SUPPORT[viewerType] || [];

        if (supportedTypes.includes('dicom') && dicomData.length > 0) {
            return { type: 'dicom', data: dicomData };
        }
        if (supportedTypes.includes('image') && imageData.length > 0) {
            return { type: 'image', data: imageData };
        }
        if (supportedTypes.includes('document') && documentData.length > 0) {
            return { type: 'document', data: documentData };
        }
        if (supportedTypes.includes('signal') && signalData.length > 0) {
            return { type: 'signal', data: signalData };
        }
        if (supportedTypes.includes('video') && videoData.length > 0) {
            return { type: 'video', data: videoData };
        }
        if (supportedTypes.includes('ehr') && ehrData.length > 0) {
            return { type: 'ehr', data: ehrData };
        }
        return null;
    };

    // Render panel content based on viewer type and data
    const renderPanelContent = (panel) => {
        const viewerType = panel.viewer_type;
        if (!viewerType) {
            return (
                <div className="custom-no-content">
                    <i className="fa fa-plus-circle"></i>
                    <p>No viewer configured</p>
                </div>
            );
        }

        // PME Pathology Viewer — standalone iframe, no data routing needed
        if (viewerType === 'pme') {
            const pmeUrl = dicomUrls['pme'] || '/pme/';
            return (
                <div className="custom-panel-content dicom-content">
                    <iframe
                        src={resolveAllowedViewerUrl(pmeUrl)}
                        title="PME Pathology Viewer"
                        className="custom-ohif-iframe"
                        allow="fullscreen"
                    />
                </div>
            );
        }

        // CVAT Video Annotation — launches CVAT project/task and embeds via iframe
        if (viewerType === 'cvat') {
            const cvatUrl = cvatUrls[panel.id];
            if (!cvatUrl && videoData.length > 0) {
                // Trigger CVAT launch for this panel
                launchCvatForPanel(panel.id);
                return (
                    <div className="custom-loading-state">
                        <div className="spinner"></div>
                        <p>Preparing CVAT video annotation...</p>
                    </div>
                );
            }
            if (!cvatUrl) {
                return (
                    <div className="custom-no-content">
                        <i className="fa fa-video-camera"></i>
                        <p>No video data available</p>
                        <small>This panel expects video files</small>
                    </div>
                );
            }
            return (
                <div className="custom-panel-content dicom-content">
                    <iframe
                        src={resolveAllowedViewerUrl(cvatUrl)}
                        title="CVAT Video Annotation"
                        className="custom-ohif-iframe"
                        allow="fullscreen"
                    />
                </div>
            );
        }

        const panelData = getPanelData(viewerType);
        if (!panelData) {
            return (
                <div className="custom-no-content">
                    <i className="fa fa-database"></i>
                    <p>No compatible data</p>
                    <small>This panel expects {VIEWER_DATA_SUPPORT[viewerType]?.join(' or ')}</small>
                </div>
            );
        }

        const currentIndex = panelStates[panel.id]?.currentIndex || 0;

        // Render DICOM panel (OHIF iframe, or trame-slicer iframe)
        if (panelData.type === 'dicom') {
            // trame-slicer has its own URL — do NOT fall back to OHIF's URL
            // (the two viewers expect different query params).
            const viewerUrl = viewerType === 'trame-slicer'
                ? dicomUrls['trame-slicer']
                : (dicomUrls[viewerType] || dicomUrls['ohif']);
            if (!viewerUrl) {
                return (
                    <div className="custom-loading-state">
                        <div className="spinner"></div>
                        <p>Loading {viewerType} viewer...</p>
                    </div>
                );
            }
            return (
                <div className="custom-panel-content dicom-content">
                    <iframe
                        src={resolveAllowedViewerUrl(viewerUrl)}
                        title={`${viewerType} Viewer`}
                        className="custom-ohif-iframe"
                        allow="fullscreen"
                    />
                </div>
            );
        }

        // Render Image panel
        if (panelData.type === 'image') {
            return (
                <ImagePanelContent
                    images={panelData.data}
                    currentIndex={currentIndex}
                    onNavigate={(direction) => navigatePanel(panel.id, direction)}
                />
            );
        }

        // Render Document panel
        if (panelData.type === 'document') {
            const documents = panelData.data;
            const documentPanelCount = getDocumentPanelCount();
            const panelState = panelStates[panel.id] || {};

            // Use assigned document index if available (for distributed documents)
            // Otherwise fall back to currentIndex for navigation.
            // Only pin panels when there are multiple document panels — with a single
            // panel we always want the user to navigate through all documents.
            let docIndex = currentIndex;
            if (
                panelState.assignedDocumentIndex !== undefined &&
                documents.length >= documentPanelCount &&
                documentPanelCount > 1
            ) {
                docIndex = panelState.assignedDocumentIndex;
            }

            const safeIndex = Math.max(0, Math.min(docIndex, documents.length - 1));
            const currentDoc = documents[safeIndex];

            if (!currentDoc) {
                return (
                    <div className="custom-no-content">
                        <i className="fa fa-file-text"></i>
                        <p>No documents available</p>
                    </div>
                );
            }

            const docType = currentDoc.documentType || currentDoc.filePath?.split('.').pop()?.toLowerCase() || '';
            const textTypes = ['txt', 'md', 'text', 'markdown'];
            const isPdf = docType === 'pdf';
            const isText = textTypes.includes(docType);

            // Determine if navigation should be shown:
            // - Hide when documents count equals or is less than document panel count (each panel shows a fixed doc)
            // - Show when there are more documents than panels and this panel needs to navigate
            const showNavigation = documents.length > documentPanelCount;

            return (
                <div className="custom-panel-content document-content">
                    {showNavigation && documents.length > 1 && (
                        <div className="document-nav">
                            <button
                                className="doc-nav-btn"
                                onClick={() => navigatePanel(panel.id, -1)}
                                disabled={safeIndex === 0}
                            >
                                <i className="fa fa-chevron-left"></i>
                            </button>
                            <span className="doc-counter">{safeIndex + 1} / {documents.length}</span>
                            <button
                                className="doc-nav-btn"
                                onClick={() => navigatePanel(panel.id, 1)}
                                disabled={safeIndex >= documents.length - 1}
                            >
                                <i className="fa fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                    {isPdf ? (
                        <div className="pdf-document">
                            <iframe
                                src={getMediaUrl(currentDoc.filePath)}
                                title={currentDoc.fileName}
                                className="pdf-iframe"
                            />
                        </div>
                    ) : isText && currentDoc.content ? (
                        <div className="text-document">
                            <div className="markdown-content">
                                <ReactMarkdown>{currentDoc.content}</ReactMarkdown>
                            </div>
                        </div>
                    ) : (
                        <div className="other-document">
                            <i className="fa fa-file"></i>
                            <p>{currentDoc.fileName}</p>
                            <a
                                href={getMediaUrl(currentDoc.filePath)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="open-btn"
                            >
                                <i className="fa fa-external-link"></i>
                                Open Document
                            </a>
                        </div>
                    )}
                </div>
            );
        }

        // Render Signal panel
        if (panelData.type === 'signal') {
            const signals = panelData.data;
            if (!signals || signals.length === 0) {
                return (
                    <div className="custom-no-content">
                        <i className="fa fa-wave-square"></i>
                        <p>No signals available</p>
                    </div>
                );
            }

            return (
                <div className="custom-panel-content signal-content">
                    <Suspense fallback={
                        <div className="custom-loading-state">
                            <div className="spinner"></div>
                            <p>Loading signal chart...</p>
                        </div>
                    }>
                        <SignalChart
                            records={signals}
                            annotations={signalAnnotations}
                            labels={labels}
                            onAnnotationCreate={handleAnnotationCreate}
                            onAnnotationDelete={handleAnnotationDelete}
                        />
                    </Suspense>
                </div>
            );
        }

        // Render EHR panel
        if (panelData.type === 'ehr') {
            const records = panelData.data;
            if (!records || records.length === 0) {
                return (
                    <div className="custom-no-content">
                        <i className="fa fa-heartbeat"></i>
                        <p>No EHR data available</p>
                    </div>
                );
            }

            const safeIndex = Math.max(0, Math.min(currentIndex, records.length - 1));
            const record = records[safeIndex];
            const content = record?.content || {};

            const toggleEhrSection = (key) => {
                setEhrExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
            };

            const formatEhrValue = (value) => {
                if (value === null) return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>null</span>;
                if (typeof value === 'boolean') return <span style={{ color: value ? '#28a745' : '#dc3545' }}>{value.toString()}</span>;
                if (typeof value === 'number') return <span style={{ color: '#0d6efd' }}>{value}</span>;
                if (typeof value === 'string') return <span style={{ color: '#fd7e14' }}>"{value}"</span>;
                return String(value);
            };

            const renderEhrValue = (key, value, depth = 0, parentKey = '') => {
                const fullKey = parentKey ? `${parentKey}.${key}` : key;
                const isExpanded = ehrExpandedSections[fullKey] ?? depth < 2;

                if (typeof value === 'object' && value !== null) {
                    const isArray = Array.isArray(value);
                    const itemCount = isArray ? value.length : Object.keys(value).length;
                    return (
                        <div key={fullKey} style={{ marginLeft: depth * 16, marginBottom: 2 }}>
                            <div
                                onClick={() => toggleEhrSection(fullKey)}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}
                            >
                                <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: 10, width: 12, color: '#6c757d' }}></i>
                                <span style={{ color: '#e9ecef', fontWeight: 500 }}>{key}</span>
                                <span style={{ color: '#6c757d', fontSize: '0.8em' }}>
                                    {isArray ? `Array[${itemCount}]` : `{${itemCount}}`}
                                </span>
                            </div>
                            {isExpanded && (
                                <div>
                                    {isArray
                                        ? value.map((item, i) => renderEhrValue(i, item, depth + 1, fullKey))
                                        : Object.entries(value).map(([k, v]) => renderEhrValue(k, v, depth + 1, fullKey))
                                    }
                                </div>
                            )}
                        </div>
                    );
                }

                return (
                    <div key={fullKey} style={{ marginLeft: depth * 16, padding: '2px 0', display: 'flex', gap: 8 }}>
                        <span style={{ color: '#adb5bd' }}>{key}:</span>
                        {formatEhrValue(value)}
                    </div>
                );
            };

            return (
                <div className="custom-panel-content" style={{ overflow: 'auto', padding: '12px', fontFamily: 'monospace', fontSize: '0.85rem', background: '#1a1d23' }}>
                    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #2d3139', paddingBottom: 8 }}>
                        <i className="fa fa-heartbeat" style={{ color: '#dc3545' }}></i>
                        <span style={{ color: '#e9ecef', fontWeight: 600 }}>{record.filename || 'EHR Record'}</span>
                        {record.patient_id && (
                            <span style={{ color: '#6c757d', fontSize: '0.85em' }}>
                                <i className="fa fa-user" style={{ marginRight: 4 }}></i>
                                {record.patient_id}
                            </span>
                        )}
                    </div>
                    <div>
                        {Object.keys(content).length === 0
                            ? <div style={{ color: '#6c757d', textAlign: 'center', padding: 20 }}>No data in this record</div>
                            : Object.entries(content).map(([k, v]) => renderEhrValue(k, v, 0, ''))
                        }
                    </div>
                </div>
            );
        }

        return null;
    };

    // Build available data types and series for annotation level filtering
    const getAvailableDataTypes = () => {
        const types = [];
        if (dicomData.length > 0) types.push('imaging');
        if (imageData.length > 0) types.push('image');
        if (documentData.length > 0) types.push('document');
        if (signalData.length > 0) types.push('signal');
        if (videoData.length > 0) types.push('video');
        return types;
    };

    const getAvailableSeries = () => {
        const series = [];
        const seenIds = new Set();

        // Add DICOM series
        dicomData.forEach((dicom, index) => {
            // DICOM data contains OHIF URL with study instance UIDs
            const studyUid = dicom.ohifUrl?.match(/StudyInstanceUIDs=([^&]+)/)?.[1];
            const id = studyUid || `dicom-${index}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: studyUid ? `DICOM Study: ${studyUid.slice(0, 20)}...` : `DICOM Study ${index + 1}`,
                    dataType: 'imaging'
                });
            }
        });

        // Add image series
        imageData.forEach((image) => {
            const id = `image-${image.id}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: image.fileName || image.filename || `Image ${image.id}`,
                    dataType: 'image'
                });
            }
        });

        // Add document series
        documentData.forEach((doc) => {
            const id = `document-${doc.id}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: doc.fileName || doc.filename || doc.title || `Document ${doc.id}`,
                    dataType: 'document'
                });
            }
        });

        return series;
    };

    // Get panel header info
    const getPanelHeader = (panel) => {
        const viewerType = panel.viewer_type;
        const panelData = getPanelData(viewerType);

        const viewerLabels = {
            'ohif': 'DICOM Viewer',
            'ohif-segmentation': 'Segmentation',
            'ohif-3d': '3D Viewer',
            'ohif-microscopy': 'Microscopy',
            'kitware-volview': 'VolView',
            'stone-web-viewer': 'Stone Viewer',
            'dv2': 'DV2 Viewer',
            'discovery-viewer-test': 'Discovery Viewer',
            'image-viewer': 'Image Viewer',
            'document-viewer': 'Document Viewer',
            'signal-viewer': 'Signal Viewer',
            'pme': 'Pathology Viewer',
            'cvat': 'CVAT Video',
            'ehr-viewer': 'EHR Viewer',
        };

        const icons = {
            'ohif': 'fa-eye',
            'ohif-segmentation': 'fa-cut',
            'ohif-3d': 'fa-cube',
            'ohif-microscopy': 'fa-microscope',
            'kitware-volview': 'fa-cube',
            'stone-web-viewer': 'fa-image',
            'dv2': 'fa-desktop',
            'discovery-viewer-test': 'fa-flask',
            'image-viewer': 'fa-image',
            'document-viewer': 'fa-file-text',
            'signal-viewer': 'fa-wave-square',
            'pme': 'fa-microscope',
            'cvat': 'fa-video-camera',
            'ehr-viewer': 'fa-heartbeat',
        };

        const label = viewerLabels[viewerType] || 'Panel';
        const icon = icons[viewerType] || 'fa-square';

        let counter = '';
        let currentItemName = '';
        if (panelData?.data?.length > 0) {
            const panelState = panelStates[panel.id] || {};
            let currentIndex = panelState.currentIndex || 0;

            // For document panels with distributed documents, use the assigned index
            if (viewerType === 'document-viewer' && panelState.assignedDocumentIndex !== undefined) {
                const documentPanelCount = getDocumentPanelCount();
                if (panelData.data.length >= documentPanelCount) {
                    currentIndex = panelState.assignedDocumentIndex;
                }
            }

            const safeIndex = Math.max(0, Math.min(currentIndex, panelData.data.length - 1));
            const currentItem = panelData.data[safeIndex];

            // Get the current item name for document/image viewers
            if (currentItem) {
                // Try to get name from various possible fields
                currentItemName = currentItem.name || currentItem.filename || currentItem.title || currentItem.fileName || '';
                // If it's a path, extract just the filename
                if (currentItemName && currentItemName.includes('/')) {
                    currentItemName = currentItemName.split('/').pop();
                }
            }

            // Only show counter if there's navigation (more documents than panels for documents)
            if (viewerType === 'document-viewer') {
                const documentPanelCount = getDocumentPanelCount();
                if (panelData.data.length > documentPanelCount) {
                    counter = `${safeIndex + 1}/${panelData.data.length}`;
                }
            } else if (panelData.data.length > 1) {
                counter = `${safeIndex + 1}/${panelData.data.length}`;
            }
        }

        return { label, icon, counter, currentItemName };
    };

    // Synchronous guard for rapid-click navigation. Each click triggers a full page
    // reload which itself re-fires launch_viewer + auth-cookie refresh. Three clicks
    // in one second produced three concurrent auth races and 401'd the session.
    // The sessionStorage lock persists across the full-page reload so the next
    // page won't accept a fresh nav click until its load cycle (and refresh) completes.
    const NAV_LOCK_KEY = 'cv_nav_lock';
    const NAV_LOCK_TTL_MS = 12000;
    const isNavigatingRef = useRef(false);
    const [isNavigating, setIsNavigating] = useState(() => {
        const lockedAt = parseInt(sessionStorage.getItem(NAV_LOCK_KEY) || '0', 10);
        return lockedAt > 0 && Date.now() - lockedAt < NAV_LOCK_TTL_MS;
    });

    // Clear the cross-page nav lock once this page finishes loading. From this
    // moment on, the next click is allowed to proceed.
    useEffect(() => {
        if (!loading) {
            sessionStorage.removeItem(NAV_LOCK_KEY);
            setIsNavigating(false);
        }
    }, [loading]);

    // Handle patient navigation
    const handlePatientChange = (newIndex) => {
        if (isNavigatingRef.current) return;
        if (newIndex < 0 || newIndex >= patientIds.length) return;
        if (newIndex === currentPatientIndex) return;

        // Cross-page-reload lock — if the previous navigation's refresh cycle hasn't
        // finished, refuse to start another one. Otherwise two POST /accounts/refresh/
        // requests with the same rotated refresh token can race and 401 the session.
        const lockedAt = parseInt(sessionStorage.getItem(NAV_LOCK_KEY) || '0', 10);
        if (lockedAt > 0 && Date.now() - lockedAt < NAV_LOCK_TTL_MS) {
            return;
        }
        sessionStorage.setItem(NAV_LOCK_KEY, String(Date.now()));

        isNavigatingRef.current = true;
        setIsNavigating(true);

        const newPatientId = patientIds[newIndex];
        const newPatientData = patientDataMap[newPatientId] || { dicom: [], image: [], document: [], video: [], ehr: [] };

        // Build new URL with updated patient data
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('patient_id', newPatientId);

        // Update data params for the new patient
        if (newPatientData.dicom && newPatientData.dicom.length > 0) {
            newUrl.searchParams.set('dicom_data', newPatientData.dicom.join(','));
        } else {
            newUrl.searchParams.delete('dicom_data');
        }

        if (newPatientData.image && newPatientData.image.length > 0) {
            newUrl.searchParams.set('image_data', newPatientData.image.join(','));
        } else {
            newUrl.searchParams.delete('image_data');
        }

        if (newPatientData.document && newPatientData.document.length > 0) {
            newUrl.searchParams.set('document_data', newPatientData.document.join(','));
        } else {
            newUrl.searchParams.delete('document_data');
        }

        if (newPatientData.video && newPatientData.video.length > 0) {
            newUrl.searchParams.set('video_data', newPatientData.video.join(','));
        } else {
            newUrl.searchParams.delete('video_data');
        }

        if (newPatientData.ehr && newPatientData.ehr.length > 0) {
            newUrl.searchParams.set('ehr_data', newPatientData.ehr.join(','));
        } else {
            newUrl.searchParams.delete('ehr_data');
        }

        // Navigate to the new URL (this will reload with correct data)
        window.location.href = newUrl.toString();
    };

    if (loading) {
        return (
            <div className="custom-viewer-page">
                <ViewerNavbar
                    studyId={studyId}
                    studyName={studyName}
                    patientId={patientId}
                    patientIds={patientIds}
                    currentPatientIndex={currentPatientIndex}
                    onPatientChange={handlePatientChange}
                    isNavigating={true}
                    availableDataTypes={[]}
                    availableSeries={[]}
                    viewerNavbarConfig={viewerNavbarConfig}
                />
                <div className="custom-viewer-loading">
                    <div className="spinner"></div>
                    <p>Loading viewer...</p>
                    <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '0.5rem' }}>
                        Please wait — do not click navigation buttons.
                    </p>
                </div>
                {/* Click-blocking overlay. Absorbs every pointer event so rapid clicks
                    on the freshly-mounted Next/Prev buttons cannot race the auth
                    refresh. Cleared automatically when loading=false. */}
                <div
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'transparent',
                        cursor: 'wait',
                        zIndex: 9999,
                    }}
                />
            </div>
        );
    }

    if (!viewerConfig || !viewerConfig.panels) {
        return (
            <div className="custom-viewer-page">
                <ViewerNavbar
                    studyId={studyId}
                    studyName={studyName}
                    patientId={patientId}
                    patientIds={patientIds}
                    currentPatientIndex={currentPatientIndex}
                    onPatientChange={handlePatientChange}
                    isNavigating={isNavigating}
                    availableDataTypes={[]}
                    availableSeries={[]}
                    viewerNavbarConfig={viewerNavbarConfig}
                />
                <div className="custom-viewer-loading">
                    <i className="fa fa-exclamation-triangle"></i>
                    <p>Invalid viewer configuration</p>
                </div>
            </div>
        );
    }

    return (
        <div className="custom-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                labelDisplayMode={labelDisplayMode}
                questions={questions}
                scores={scores}
                patientId={patientId}
                patientIds={patientIds}
                currentPatientIndex={currentPatientIndex}
                onPatientChange={handlePatientChange}
                isNavigating={isNavigating}
                shareAnnotations={shareAnnotations}
                availableDataTypes={getAvailableDataTypes()}
                availableSeries={getAvailableSeries()}
                viewerNavbarConfig={viewerNavbarConfig}
            />

            <div className="custom-viewer-content">
                {/* Patient Header */}
                <div className="custom-patient-header">
                    <div className="patient-info">
                        <i className="fa fa-user-circle"></i>
                        <div>
                            <span className="patient-label">Patient ID</span>
                            <span className="patient-id-value">{patientId}</span>
                        </div>
                    </div>
                    <div className="viewer-name">
                        <i className="fa fa-tv"></i>
                        <span>{viewerName}</span>
                    </div>
                </div>

                {/* Dynamic Panel Layout */}
                <div className="custom-panel-container">
                    {viewerConfig.panels.map((panel) => {
                        const headerInfo = getPanelHeader(panel);
                        return (
                            <div
                                key={panel.id}
                                className="custom-panel"
                                style={{
                                    left: `${panel.x}%`,
                                    top: `${panel.y}%`,
                                    width: `${panel.w}%`,
                                    height: `${panel.h}%`,
                                }}
                            >
                                <div className="custom-panel-header">
                                    <i className={`fa ${headerInfo.icon}`}></i>
                                    <span>{headerInfo.label}</span>
                                    {headerInfo.currentItemName && (
                                        <span className="panel-item-name" title={headerInfo.currentItemName}>
                                            - {headerInfo.currentItemName}
                                        </span>
                                    )}
                                    {headerInfo.counter && (
                                        <span className="panel-counter">{headerInfo.counter}</span>
                                    )}
                                </div>
                                {renderPanelContent(panel)}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CustomViewer;
